import { atom } from "xoid";
import { getFeatureFlags } from "@/lib/runtime-config";
import { getCashAgentUrl } from "@/lib/runtime-config";

export interface RecyclerFillStatus {
  active: boolean;
  sessionId: string | null;
  sessionInserted: Record<string, number>;
  totalRecycler: Record<string, number>;
  error: string | null;
}

export type RecyclerFillEvent =
  | { type: "connected"; clientId: string }
  | { type: "navigate"; path: string }
  | { type: "update"; status: RecyclerFillStatus }
  | { type: "stopped"; finalStatus: RecyclerFillStatus }
  | { type: "error"; message: string };

export interface RecyclerFillState {
  events: RecyclerFillEvent[];
  status: RecyclerFillStatus | null;
  connected: boolean;
  error: string | null;
}

const initialState: RecyclerFillState = {
  events: [],
  status: null,
  connected: false,
  error: null,
};

export const recyclerFillAtom = atom<RecyclerFillState>(initialState);

let eventSource: EventSource | null = null;

function handleEvent(event: RecyclerFillEvent) {
  const current = recyclerFillAtom.value;

  switch (event.type) {
    case "connected":
      recyclerFillAtom.set({
        ...current,
        events: [...current.events, event],
        connected: true,
        error: null,
      });
      break;
    case "navigate":
      recyclerFillAtom.set({
        ...current,
        events: [...current.events, event],
      });
      break;
    case "update":
      recyclerFillAtom.set({
        ...current,
        events: [...current.events, event],
        status: event.status,
      });
      break;
    case "stopped":
      recyclerFillAtom.set({
        ...current,
        events: [...current.events, event],
        status: event.finalStatus,
      });
      break;
    case "error":
      recyclerFillAtom.set({
        ...current,
        events: [...current.events, event],
        error: event.message,
      });
      break;
  }
}

export function subscribeToRecyclerFillEvents() {
  if (eventSource) {
    return;
  }

  const url = `${getCashAgentUrl()}/recycler-fill/events`;
  eventSource = new EventSource(url);

  eventSource.onopen = () => {
    console.log("SSE connection opened");
    recyclerFillAtom.set({
      ...recyclerFillAtom.value,
      connected: true,
      error: null,
    });
  };

  eventSource.onmessage = (event) => {
    try {
      const data: RecyclerFillEvent = JSON.parse(event.data);
      console.log("SSE event received:", data);
      handleEvent(data);
    } catch (e) {
      console.error("Failed to parse SSE event:", e);
    }
  };

  eventSource.onerror = (e) => {
    if (recyclerFillAtom.value.connected) {
      console.error("SSE connection error:", e);
    }
    recyclerFillAtom.set({
      ...recyclerFillAtom.value,
      connected: false,
      error: "Connection lost",
    });
  };
}

export function unsubscribeFromRecyclerFillEvents() {
  if (eventSource) {
    console.log("Closing SSE connection");
    eventSource.close();
    eventSource = null;
  }
}

export async function stopRecyclerFillSession() {
  const response = await fetch(
    `${getCashAgentUrl()}/recycler-fill/stop`,
    {
      method: "POST",
    },
  );
  return response.json();
}

export async function getRecyclerFillStatus(): Promise<RecyclerFillStatus> {
  const response = await fetch(
    `${getCashAgentUrl()}/recycler-fill/status`,
  );
  const data: RecyclerFillStatus = await response.json();
  recyclerFillAtom.set({
    ...recyclerFillAtom.value,
    status: data,
  });
  return data;
}

export function initRecyclerFillEvents() {
  if (getFeatureFlags().recyclerFillEvents) {
    subscribeToRecyclerFillEvents();
  }
}
